/**
 * Small notes a sign-in leaves for itself, so the work it promised still
 * happens after the page it started on is gone.
 *
 * Google and Apple sign in by leaving the site and coming back on a fresh page
 * load, so nothing in React state survives the trip. sessionStorage does: it
 * lives as long as the tab, which is exactly the round trip, and it is gone
 * when the tab is closed, so a note can never fire on a later visit. Each
 * note also carries its own time and is ignored once stale, so an abandoned
 * Google screen cannot greet someone half an hour later.
 *
 *  - welcome: show "You're signed in" once, when the session arrives.
 *  - pending save: the heart tapped while signed out, saved once signed in.
 *  - pending claims: a guest booking to put on the account once signed in.
 */

export const WELCOME_KEY = "mj-auth-welcome";
export const PENDING_SAVE_KEY = "mj-pending-save";
export const PENDING_CLAIMS_KEY = "mj-pending-claims";
/** Fired on window when a welcome note is written, for a listener already mounted. */
export const WELCOME_EVENT = "mj-auth-welcome";

const WELCOME_TTL_MS = 15 * 60 * 1000;
const PENDING_TTL_MS = 30 * 60 * 1000;

type Stamped<T> = T & { at: number };

function read<T>(key: string, ttl: number): Stamped<T> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as Stamped<T>;
    if (typeof value?.at !== "number" || Date.now() - value.at > ttl) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function write(key: string, value: object) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ ...value, at: Date.now() }));
  } catch {
    // Private mode or a full quota: the extra is lost, the sign-in is not.
  }
}

function remove(key: string) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/* ------------------------------------------------------------ welcome */

export type SignInMethod = "email" | "google" | "apple";

export function markWelcome(via: SignInMethod) {
  write(WELCOME_KEY, { via });
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(WELCOME_EVENT));
}

export function clearWelcome() {
  remove(WELCOME_KEY);
}

/** Reads and forgets the note, so the greeting shows once. */
export function takeWelcome(): { via: SignInMethod } | null {
  const note = read<{ via: SignInMethod }>(WELCOME_KEY, WELCOME_TTL_MS);
  remove(WELCOME_KEY);
  return note;
}

/* ------------------------------------------------------- pending save */

export interface PendingSave {
  slug: string;
  title: string;
  image?: string;
}

export function setPendingSave(item: PendingSave) {
  write(PENDING_SAVE_KEY, item);
}

export function clearPendingSave() {
  remove(PENDING_SAVE_KEY);
}

export function takePendingSave(): PendingSave | null {
  const note = read<PendingSave>(PENDING_SAVE_KEY, PENDING_TTL_MS);
  remove(PENDING_SAVE_KEY);
  return note && typeof note.slug === "string" ? { slug: note.slug, title: note.title, image: note.image } : null;
}

/* ----------------------------------------------------- pending claims */

export interface PendingClaim {
  reference: string;
  email: string;
}

export function addPendingClaim(claim: PendingClaim) {
  const list = readPendingClaims().filter((c) => c.reference !== claim.reference);
  write(PENDING_CLAIMS_KEY, { items: [...list, claim] });
}

export function removePendingClaim(reference: string) {
  const list = readPendingClaims().filter((c) => c.reference !== reference);
  if (list.length === 0) remove(PENDING_CLAIMS_KEY);
  else write(PENDING_CLAIMS_KEY, { items: list });
}

export function readPendingClaims(): PendingClaim[] {
  const note = read<{ items?: PendingClaim[] }>(PENDING_CLAIMS_KEY, PENDING_TTL_MS);
  return Array.isArray(note?.items) ? note.items.filter((c) => c?.reference && c?.email) : [];
}

/* -------------------------------------------------------------- names */

/**
 * The first name to greet with, or null. better-auth fills `name` with the
 * email on some older sign-ups, and a code sign-up leaves it empty.
 */
export function greetingName(user: { name?: string | null; email?: string | null } | null | undefined): string | null {
  const name = user?.name?.trim();
  if (!name || name.includes("@")) return null;
  if (user?.email && name.toLowerCase() === user.email.trim().toLowerCase()) return null;
  return name.split(/\s+/)[0] || null;
}

/** An account made moments ago: the sign-in that created it. */
export function isNewAccount(user: { createdAt?: string | Date | null } | null | undefined, withinMs = 10 * 60 * 1000): boolean {
  if (!user?.createdAt) return false;
  const created = new Date(user.createdAt).getTime();
  return Number.isFinite(created) && Date.now() - created < withinMs;
}
